import React, { useEffect, useState } from 'react';
import { getProjectActivity } from 'api/activity';
import { isErrorResponse } from 'utils/apiUtils';
import { ActivityPoint, ActivitySeriesResponse } from 'types';

type MomentumChartProps = {
    projectId: string;
    ostId: string;
    days?: number;
};

const CHART_HEIGHT = 100;
const CHART_WIDTH = 240;
const BAR_GAP = 2;
// Reserved margins so the plot area leaves room for axis labels/gridlines/title.
const AXIS_LABEL_WIDTH = 30;
const AXIS_LABEL_HEIGHT = 14;
const TOTAL_WIDTH = CHART_WIDTH + AXIS_LABEL_WIDTH;
const TOTAL_HEIGHT = CHART_HEIGHT + AXIS_LABEL_HEIGHT;

/** Renders an ISO "YYYY-MM-DD" activity date as a short "Mon D" label, parsed as UTC to avoid local-timezone day shift. */
function formatAxisDate(iso: string): string {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    });
}

/** Today's date at UTC midnight — matches the UTC calendar days the server groups activity into. */
function todayUTC(): Date {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
}

/**
 * The server only returns rows for days that had activity (a sparse aggregate — see
 * server/server.js's `/activity` route), so a tree active on only one day out of the
 * requested window would otherwise render as a single bar stretched across the full chart
 * width. Filling in the missing days as zero-count keeps every bar's width consistent and the
 * x-axis a true, evenly-spaced calendar timeline.
 */
function fillMissingDays(series: ActivityPoint[], days: number): ActivityPoint[] {
    const countByDate = new Map(series.map((point) => [point.date, point.count]));
    const anchor = todayUTC();
    const points: ActivityPoint[] = [];
    for (let offset = days - 1; offset >= 0; offset--) {
        const date = new Date(anchor);
        date.setUTCDate(date.getUTCDate() - offset);
        const iso = date.toISOString().slice(0, 10);
        points.push({ date: iso, count: countByDate.get(iso) ?? 0 });
    }
    return points;
}

/**
 * Small SVG bar chart of daily "meaningful change" counts (see
 * server/activityTracker.js), built the same dependency-free, fluid-viewBox
 * way as MiniPieChart — the project has no charting library installed.
 */
export default function MomentumChart({ projectId, ostId, days = 30 }: MomentumChartProps) {
    const [series, setSeries] = useState<ActivityPoint[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setFailed(false);

        getProjectActivity(projectId, ostId, days).then((result) => {
            if (cancelled) return;
            setLoading(false);
            if (isErrorResponse(result)) {
                setFailed(true);
                return;
            }
            setSeries((result as ActivitySeriesResponse).series);
        });

        return () => { cancelled = true; };
    }, [projectId, ostId, days]);

    // Matches the chart's own viewBox aspect ratio, so every non-chart state below reserves
    // exactly the height the loaded SVG will end up at and the panel never resizes once it loads.
    const placeholderStyle = { aspectRatio: `${TOTAL_WIDTH} / ${TOTAL_HEIGHT}` };

    if (loading) {
        return (
            <div className='momentum-chart__placeholder' style={placeholderStyle}>
                <span className='momentum-chart__spinner' aria-label='Loading momentum chart' />
            </div>
        );
    }
    if (failed) {
        return (
            <div className='momentum-chart__placeholder' style={placeholderStyle}>
                <p className='momentum-chart__empty'>Couldn't load activity.</p>
            </div>
        );
    }

    const rawPoints = series ?? [];
    const total = rawPoints.reduce((sum, point) => sum + point.count, 0);
    if (total === 0) {
        return (
            <div className='momentum-chart__placeholder' style={placeholderStyle}>
                <p className='momentum-chart__empty'>No recent activity yet.</p>
            </div>
        );
    }

    const points = fillMissingDays(rawPoints, days);
    const maxCount = Math.max(...points.map((point) => point.count), 1);
    const barWidth = points.length > 0 ? CHART_WIDTH / points.length - BAR_GAP : 0;
    const midGridY = CHART_HEIGHT / 2;
    const axisTitleX = 9;

    return (
        <svg viewBox={`0 0 ${TOTAL_WIDTH} ${TOTAL_HEIGHT}`} className='momentum-chart'>
            {/* Light reference gridlines (top + mid), drawn behind the bars, plus their value labels. */}
            <line x1={AXIS_LABEL_WIDTH} y1={1} x2={TOTAL_WIDTH} y2={1} className='momentum-chart__gridline' />
            <line x1={AXIS_LABEL_WIDTH} y1={midGridY} x2={TOTAL_WIDTH} y2={midGridY} className='momentum-chart__gridline' />
            <text
                x={axisTitleX}
                y={CHART_HEIGHT / 2}
                transform={`rotate(-90 ${axisTitleX} ${CHART_HEIGHT / 2})`}
                className='momentum-chart__axis-title'
            >
                changes/day
            </text>
            <text x={AXIS_LABEL_WIDTH - 4} y={midGridY} className='momentum-chart__axis-label momentum-chart__axis-label--y'>
                {Math.round(maxCount / 2)}
            </text>
            <text x={AXIS_LABEL_WIDTH - 4} y={4} className='momentum-chart__axis-label momentum-chart__axis-label--y'>
                {maxCount}
            </text>
            <text x={AXIS_LABEL_WIDTH - 4} y={CHART_HEIGHT} className='momentum-chart__axis-label momentum-chart__axis-label--y'>
                0
            </text>

            {points.map((point, index) => {
                const barHeight = (point.count / maxCount) * CHART_HEIGHT;
                const x = AXIS_LABEL_WIDTH + index * (barWidth + BAR_GAP);
                const y = CHART_HEIGHT - barHeight;
                return (
                    <rect
                        key={point.date}
                        x={x}
                        y={y}
                        width={Math.max(barWidth, 0)}
                        height={barHeight}
                        rx={1}
                        className='momentum-chart__bar'
                    >
                        <title>{`${point.date}: ${point.count} change${point.count === 1 ? '' : 's'}`}</title>
                    </rect>
                );
            })}

            {/* X-axis baseline, plus the range's start/end dates so the bars read as a timeline. */}
            <line x1={AXIS_LABEL_WIDTH} y1={CHART_HEIGHT} x2={TOTAL_WIDTH} y2={CHART_HEIGHT} className='momentum-chart__axis-line' />
            <text x={AXIS_LABEL_WIDTH} y={TOTAL_HEIGHT - 2} className='momentum-chart__axis-label momentum-chart__axis-label--x-start'>
                {formatAxisDate(points[0].date)}
            </text>
            <text x={TOTAL_WIDTH} y={TOTAL_HEIGHT - 2} className='momentum-chart__axis-label momentum-chart__axis-label--x-end'>
                {formatAxisDate(points[points.length - 1].date)}
            </text>
        </svg>
    );
}
